import { Switch } from '@mantine/core'
import { IconHeatmap } from 'components/icons/IconHeatmap'
import { IconAndTitle } from 'components/SideNav/IconAndTitle/IconAndTitle'
import { translate } from 'i18n/i18n'
import { colors } from 'settings/colors'
import { HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD } from 'settings/hubConnectorEnvironment'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import {
  degreeDaysRecord,
  larvalTrendRecord,
  phenologyRecord,
  trapCatchesRecord,
} from 'stores/selectedValueGroupsStore/insectValueGroupRecords'
import type { TValueGroup } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { usePrimaryValueGroup } from 'utils/usePrimaryValueGroup'
import { EquipmentFilters } from '../../../ServiceCenter/EquipmentFilter/EquipmentFilters'
import { HeatmapControlsRow } from '../HeatmapControlsRow/HeatmapControlsRow'
import { AggregateSwitch } from './AggregateSwitch/AggregateSwitch'
import { NdviMapControls } from './NdviMapControls/NdviMapControls'

export const includeAggregateSwitch: Record<TValueGroup, boolean> = {
  air_temperature: true,
  almond_bloom: false,
  almond_hull_split: true,
  alternaria: false,
  atmospheric_pressure: true,
  bee_hours: false,
  cherry_powdery_mildew: false,
  chill: true,
  conditions: false,
  ...degreeDaysRecord(() => true),
  dew_point: true,
  equipment_status: false,
  evapotranspiration: false,
  fire_blight: true,
  fruit_growth: false,
  grape_powdery_mildew: false,
  plant_stress: false,
  humidity: true,
  irrigation_activity_pressure: false,
  irrigation_activity_zones: false,
  irrigation_activity_flow_meters: false,
  ...larvalTrendRecord(() => false),
  leaf_wetness: false,
  precipitation: false,
  soil: false,
  spray_conditions: false,
  ...trapCatchesRecord(() => true),
  ...phenologyRecord(() => true),
  wet_bulb: true,
  wind: false,
  wind_machine: false,
  ndvi: false,
}

const excludeMapLabelSwitch: { [key in TValueGroup]?: boolean } = {
  equipment_status: true,
}

export const MapControlItems = () => {
  const primaryValueGroup = usePrimaryValueGroup()
  const showMapBadgeNames = mapControlsStore.useSelector((s) => s.showMapBadgeNames)
  const showPropertyPlayback = smallStore.useSelector((s) => s.showPropertyPlayback)

  let filterRow: React.ReactNode = <HeatmapControlsRow />

  if (primaryValueGroup === 'equipment_status') filterRow = <EquipmentFilters />

  const showSwitchRow =
    (!excludeMapLabelSwitch[primaryValueGroup as TValueGroup] ||
      includeAggregateSwitch[primaryValueGroup as TValueGroup]) &&
    !showPropertyPlayback

  const showPropertyPlaybackButton =
    !HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD &&
    primaryValueGroup === 'air_temperature' && // currently only for temperature
    !showPropertyPlayback

  const enableNdviMapControls = !HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD && !showPropertyPlayback

  return (
    <>
      {showSwitchRow && (
        <div css={{ flexWrap: 'nowrap', display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
          {excludeMapLabelSwitch[primaryValueGroup as TValueGroup] || (
            <div>
              <Switch
                label={translate.phrases.banyanApp('Map Labels')}
                checked={showMapBadgeNames}
                onChange={(event) =>
                  mapControlsStore.setState((s) => ({
                    ...s,
                    showMapBadgeNames: event.currentTarget.checked,
                  }))
                }
                size="xs"
                styles={{
                  label: {
                    color: HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD ? colors.midnight : colors.white,
                    cursor: 'pointer',
                    fontSize: 13,
                    paddingLeft: '0.5rem',
                  },
                  track: {
                    cursor: 'pointer',
                    backgroundColor: showMapBadgeNames ? colors.green : undefined,
                    borderColor: showMapBadgeNames ? colors.green : undefined,
                  },
                }}
              />
            </div>
          )}
          <AggregateSwitch
            includeAggregateSwitch={!!includeAggregateSwitch[primaryValueGroup as TValueGroup]}
            displayAsHeatmapSwitch
          />
        </div>
      )}
      {filterRow}

      {showPropertyPlaybackButton && (
        <span css={{ color: 'white', textDecoration: 'underline' }}>
          <IconAndTitle
            icon={<IconHeatmap />}
            onClick={() => smallStore.setState((s) => ({ ...s, showPropertyPlayback: true }))}
            title={translate.phrases.banyanApp('View Property Playback')}
          />
        </span>
      )}
      {enableNdviMapControls && <NdviMapControls />}
    </>
  )
}
